/**
 * Casa de Flopi — upsell on Horizon's native cart drawer.
 * The cart/section event contract is identical to native product-form.js.
 * Event delegation survives Shopify Section Rendering API morphs.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ERROR_FALLBACK = 'No pudimos agregar el producto. Inténtalo nuevamente.';

function getCartPath() {
  return globalThis.Theme?.routes?.cart_url || `${globalThis.Shopify?.routes?.root || '/'}cart`;
}

function renderError(form, message) {
  const errorNode = form.querySelector('.flopi-upsell__error');
  if (!errorNode) return;
  errorNode.textContent = message || ERROR_FALLBACK;
  errorNode.hidden = false;
}

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-flopi-upsell-variant]')) return;
  const card = select.closest('.flopi-upsell__card');
  const price = card?.querySelector('.flopi-upsell__price');
  const option = select.selectedOptions[0];
  if (price && option?.dataset.price) price.textContent = option.dataset.price;
});

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('#cart-drawer .flopi-upsell__form')) return;
  event.preventDefault();
  if (form.dataset.loading === 'true') return;
  const variantId = Number(new FormData(form).get('id'));
  if (!Number.isSafeInteger(variantId) || variantId <= 0) {
    renderError(form, 'Selecciona una variante disponible.');
    return;
  }
  form.dataset.loading = 'true';
  const button = form.querySelector('button[type="submit"]');
  const originalLabel = button?.innerHTML;
  const errorNode = form.querySelector('.flopi-upsell__error');
  if (errorNode) { errorNode.textContent = ''; errorNode.hidden = true; }
  if (button) { button.disabled = true; button.textContent = 'Agregando…'; }

  const sectionIds = [...new Set(
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId).filter(Boolean)
  )];
  if (!sectionIds.includes('cart-drawer-section')) sectionIds.push('cart-drawer-section');
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'cart',
    lines: [{ merchandiseId: String(variantId), quantity: 1 }],
    promise: deferred.promise,
  }));

  try {
    const response = await fetch(globalThis.Theme?.routes?.cart_add_url || `${globalThis.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, sections: sectionIds, sections_url: location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(typeof data.description === 'string' ? data.description : (data.message || ERROR_FALLBACK));
    }
    const cartResponse = await fetch(`${getCartPath()}.json`, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
      cache: 'no-store',
    });
    if (!cartResponse.ok) throw new Error(ERROR_FALLBACK);
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: data.sections || undefined,
        items: cart.items,
        itemCount: cart.item_count,
        productId: form.closest('[data-product-id]')?.dataset.productId,
        source: 'flopi-cart-upsell',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    const message = error instanceof Error ? error.message : ERROR_FALLBACK;
    renderError(form, message);
    form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
  } finally {
    delete form.dataset.loading;
    if (button && button.isConnected) {
      button.disabled = false;
      if (originalLabel) button.innerHTML = originalLabel;
    }
  }
});
